import {
  BoxGeometry,
  Cartesian2,
  Cartesian3,
  Color,
  ColorGeometryInstanceAttribute,
  CornerType,
  GeometryInstance,
  Matrix4,
  PerInstanceColorAppearance,
  PolylineColorAppearance,
  PolylineGeometry,
  PolylineVolumeGeometry,
  Primitive,
  PrimitiveCollection,
} from 'cesium';
import { config } from '../app/config';
import { gaugeForFeature } from '../app/config';
import { buildOhle } from './catenary';
import type { RailAttributes, TrackStation } from './types';

const BALLAST_COLOR = Color.fromCssColorString('#6b6b6b');
const RAIL_COLOR = Color.fromCssColorString('#9a9a9a');
const SLEEPER_COLOR = Color.fromCssColorString('#4a3b2f');
const MAST_COLOR = Color.fromCssColorString('#3a3a3a');
const WIRE_COLOR = Color.fromCssColorString('#d8d8d8');

/**
 * Build the full near-LOD track structure for one feature's stations and return
 * a PrimitiveCollection that the LOD manager can add/remove atomically.
 */
export function buildTrackStructure(
  stations: TrackStation[],
  attrs: RailAttributes,
): PrimitiveCollection {
  const collection = new PrimitiveCollection();
  if (stations.length < 2) return collection;

  const halfGauge = gaugeForFeature(attrs.gauge) / 2;

  collection.add(buildBallast(stations));
  collection.add(buildRails(stations, halfGauge));
  collection.add(buildSleepers(stations));

  if (attrs.electrified) {
    const ohle = buildOhle(stations);
    collection.add(buildStruts(ohle.masts.map(({ base, top }) => ({ from: base, to: top })), 0.2));
    collection.add(buildStruts(ohle.arms, 0.08));
    collection.add(buildWires([...ohle.contact, ...ohle.top]));
  }

  return collection;
}

/** Ballast: trapezoidal prism extruded along the centreline below rail level. */
function buildBallast(stations: TrackStation[]): Primitive {
  const t = config.track;
  const baseDrop = t.railProfileHeight + t.sleeperDepth;
  const polyline = stations.map((s) =>
    Cartesian3.add(
      s.position,
      Cartesian3.multiplyByScalar(s.up, -baseDrop, new Cartesian3()),
      new Cartesian3(),
    ),
  );
  const shape: Cartesian2[] = [
    new Cartesian2(-t.ballastTopWidth / 2, 0),
    new Cartesian2(t.ballastTopWidth / 2, 0),
    new Cartesian2(t.ballastBottomWidth / 2, -t.ballastHeight),
    new Cartesian2(-t.ballastBottomWidth / 2, -t.ballastHeight),
  ];
  return volumePrimitive(polyline, shape, BALLAST_COLOR);
}

/** Rails: two offset volumes with a simplified profile, top at rail level. */
function buildRails(stations: TrackStation[], halfGauge: number): Primitive {
  const t = config.track;
  const w = t.railProfileWidth;
  const h = t.railProfileHeight;
  const shape: Cartesian2[] = [
    new Cartesian2(-w / 2, -h),
    new Cartesian2(w / 2, -h),
    new Cartesian2(w / 2, 0),
    new Cartesian2(-w / 2, 0),
  ];

  const instances: GeometryInstance[] = [];
  for (const sign of [-1, 1]) {
    const polyline = stations.map((s) =>
      Cartesian3.add(
        s.position,
        Cartesian3.multiplyByScalar(s.right, sign * halfGauge, new Cartesian3()),
        new Cartesian3(),
      ),
    );
    instances.push(
      new GeometryInstance({
        geometry: new PolylineVolumeGeometry({
          polylinePositions: polyline,
          shapePositions: shape,
          cornerType: CornerType.MITERED,
          vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
        }),
        attributes: {
          color: ColorGeometryInstanceAttribute.fromColor(RAIL_COLOR),
        },
      }),
    );
  }
  return new Primitive({
    geometryInstances: instances,
    appearance: new PerInstanceColorAppearance({ closed: true }),
    asynchronous: false,
  });
}

/** Sleepers: one batched box per station, long axis along `right`. */
function buildSleepers(stations: TrackStation[]): Primitive {
  const t = config.track;
  const box = BoxGeometry.fromDimensions({
    dimensions: new Cartesian3(t.sleeperLength, t.sleeperWidth, t.sleeperDepth),
    vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
  });
  const dropToSleeperCentre = t.railProfileHeight + t.sleeperDepth / 2;

  const instances: GeometryInstance[] = stations.map((s) => {
    const centre = Cartesian3.add(
      s.position,
      Cartesian3.multiplyByScalar(s.up, -dropToSleeperCentre, new Cartesian3()),
      new Cartesian3(),
    );
    return new GeometryInstance({
      geometry: box,
      modelMatrix: basisMatrix(s.right, s.tangent, s.up, centre),
      attributes: {
        color: ColorGeometryInstanceAttribute.fromColor(SLEEPER_COLOR),
      },
    });
  });

  return new Primitive({
    geometryInstances: instances,
    appearance: new PerInstanceColorAppearance(),
    asynchronous: false,
  });
}

/** Structural members (masts, cantilever arms): boxes between two points. */
function buildStruts(
  struts: Array<{ from: Cartesian3; to: Cartesian3 }>,
  thickness: number,
): Primitive {
  const instances: GeometryInstance[] = struts.map(({ from, to }) => {
    const length = Cartesian3.distance(from, to);
    const centre = Cartesian3.midpoint(from, to, new Cartesian3());
    const axis = Cartesian3.normalize(
      Cartesian3.subtract(to, from, new Cartesian3()),
      new Cartesian3(),
    );
    // Build an arbitrary orthonormal basis around `axis`.
    const ref = Math.abs(axis.z) < 0.99 ? Cartesian3.UNIT_Z : Cartesian3.UNIT_X;
    const right = Cartesian3.normalize(
      Cartesian3.cross(ref, axis, new Cartesian3()),
      new Cartesian3(),
    );
    const fwd = Cartesian3.cross(axis, right, new Cartesian3());
    return new GeometryInstance({
      geometry: BoxGeometry.fromDimensions({
        dimensions: new Cartesian3(thickness, thickness, length),
        vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
      }),
      modelMatrix: basisMatrix(right, fwd, axis, centre),
      attributes: { color: ColorGeometryInstanceAttribute.fromColor(MAST_COLOR) },
    });
  });
  return new Primitive({
    geometryInstances: instances,
    appearance: new PerInstanceColorAppearance(),
    asynchronous: false,
  });
}

/** Wires (contact + top/catenary): straight polylines between masts. */
function buildWires(spans: Array<{ points: Cartesian3[] }>): Primitive {
  const instances = spans.map(
    (span) =>
      new GeometryInstance({
        geometry: new PolylineGeometry({
          positions: span.points,
          width: 1.5,
          vertexFormat: PolylineColorAppearance.VERTEX_FORMAT,
        }),
        attributes: { color: ColorGeometryInstanceAttribute.fromColor(WIRE_COLOR) },
      }),
  );
  return new Primitive({
    geometryInstances: instances,
    appearance: new PolylineColorAppearance(),
    asynchronous: false,
  });
}

// --- helpers ---------------------------------------------------------------

/** A model matrix whose columns are the basis vectors x,y,z at `translation`. */
function basisMatrix(
  x: Cartesian3,
  y: Cartesian3,
  z: Cartesian3,
  translation: Cartesian3,
): Matrix4 {
  // Cesium's Matrix4 constructor takes values in row-major order.
  return new Matrix4(
    x.x, y.x, z.x, translation.x,
    x.y, y.y, z.y, translation.y,
    x.z, y.z, z.z, translation.z,
    0, 0, 0, 1,
  );
}

function volumePrimitive(
  polyline: Cartesian3[],
  shape: Cartesian2[],
  color: Color,
): Primitive {
  return new Primitive({
    geometryInstances: new GeometryInstance({
      geometry: new PolylineVolumeGeometry({
        polylinePositions: polyline,
        shapePositions: shape,
        cornerType: CornerType.MITERED,
        vertexFormat: PerInstanceColorAppearance.VERTEX_FORMAT,
      }),
      attributes: { color: ColorGeometryInstanceAttribute.fromColor(color) },
    }),
    appearance: new PerInstanceColorAppearance({ closed: true }),
    asynchronous: false,
  });
}
